/**
 * Shared literals for the Table module (ARIA, breakpoints, pagination math).
 * Prefer importing these instead of duplicating strings across `Table.tsx` and `table.utils.ts`.
 */

/** Pixel width for `md` — matches v3 theme breakpoint used by `TABLE_MD_UP_MEDIA_QUERY`. */
export const TABLE_BREAKPOINT_MD_PX = 960;

/**
 * Media query evaluated by `useIsMdUp` to set `Table.Provider` context `isDesktop`.
 * Use the same breakpoint string if you need to align custom layouts with the table.
 */
export const TABLE_MD_UP_MEDIA_QUERY = `(min-width: ${TABLE_BREAKPOINT_MD_PX}px)`;

/** Accessible names for navigational regions (e.g. pagination `<nav>`). */
export const TABLE_ARIA_LABELS = {
  paginationNav: 'Table pagination',
} as const;

/** Default link text for prev/next in `Table.Pagination` `mode="links"` when `label` is missing. */
export const TABLE_PAGINATION_NAV_FALLBACK = {
  previous: 'Previous',
  next: 'Next',
} as const;

/** Error thrown by `useTableContext` when the hook runs outside `Table.Provider`. */
export const TABLE_CONTEXT_ERROR = 'useTableContext must be used within Table.Provider';

/** Internal sort direction values stored in `TableSortState`. */
export const TABLE_SORT_DIRECTION = {
  asc: 'asc',
  desc: 'desc',
} as const;

/** Maps sort state to `aria-sort` token on column headers. */
export const TABLE_ARIA_SORT = {
  none: 'none',
  ascending: 'ascending',
  descending: 'descending',
} as const;

/**
 * First page index in **1-based** pagination (portal convention).
 * Also used as the minimum page count when `totalItems === 0`.
 */
export const TABLE_FIRST_PAGE_ONE_BASED = 1;

/** Lower bound for a valid current page (same as {@link TABLE_FIRST_PAGE_ONE_BASED}). */
export const TABLE_PAGINATION_MIN_PAGE = TABLE_FIRST_PAGE_ONE_BASED;

/**
 * Offset between **0-based** `Pagination` page index (gatorcomponents) and **1-based** `page` / `onPageChange`.
 * @example `safePage - TABLE_ZERO_TO_ONE_BASED_OFFSET` yields the zero-based index passed to `Pagination`.
 */
export const TABLE_ZERO_TO_ONE_BASED_OFFSET = TABLE_FIRST_PAGE_ONE_BASED;
